import React, { useState, useEffect, useRef } from 'react';
import { Send, Sparkles } from 'lucide-react';
import useUnifiedWebSocket from '../hooks/useUnifiedWebSocket';
import { projectAPI } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import StreamingContentRenderer from './StreamingContentRenderer';

const ProjectReportChat = ({ projectId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const messagesEndRef = useRef(null);
  
  // WebSocket for streaming
  const unified = useUnifiedWebSocket(null);
  
  // Fetch history and chatId on mount
  useEffect(() => {
    if (projectId) {
        // Don't show loading for history fetch to avoid flicker, just load it
        projectAPI.getReportChat(projectId)
            .then(res => {
                if (res.data && res.data.chatId) {
                    setChatId(res.data.chatId);
                    setMessages(res.data.history || []);
                }
            })
            .catch(err => console.error("Failed to load report chat:", err));
    }
  }, [projectId]);
  
  // Subscribe to streaming channel
  useEffect(() => {
    if (!chatId) return;
    
    const channel = `chat_${chatId}`; 
    
    const unsubscribe = unified.subscribeToChannel(channel);
    
    const onStreamChunk = (data) => {
        // data: { chunk: string, messageId: string, ... }
        const chunk = data.chunk;
        if (!chunk) return;
        
        setMessages(prev => {
            const last = prev[prev.length - 1];
            // If last message is from model and recent (assumed streaming), append
            if (last && last.role === 'model' && !last.isComplete) {
                return [
                    ...prev.slice(0, -1),
                    { ...last, content: last.content + chunk }
                ];
            } else {
                // If last message was user, or completed model message, create new
                // BUT: Avoid creating multiple new messages for same stream
                // The logic here is tricky without messageId tracking.
                // We assume the component state is consistent.
                // Ideally we should track currentStreamingMessageId
                return [...prev, { role: 'model', content: chunk, isComplete: false }];
            }
        });
    };
    
    const off1 = unified.registerHandler('stream_chunk', onStreamChunk, channel);
    
    return () => {
        if (off1) off1();
        if (unsubscribe) unsubscribe();
    };
  }, [chatId, unified]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    
    try {
        await projectAPI.sendReportMessage(projectId, userMsg.content);
    } catch (err) {
        console.error("Failed to send message:", err);
        setMessages(prev => [...prev, { role: 'model', content: "Sorry, I couldn't process that request." }]);
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[600px] bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-2">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Project Analyst</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Ask questions about your project history and data</p>
                </div>
            </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50 dark:bg-gray-900/50">
            {messages.length === 0 && !isLoading && (
                <div className="text-center py-12">
                    <div className="inline-flex justify-center items-center w-16 h-16 rounded-full bg-purple-50 dark:bg-purple-900/20 mb-4">
                        <Sparkles className="w-8 h-8 text-purple-500/50" />
                    </div>
                    <h4 className="text-gray-900 dark:text-white font-medium mb-2">No messages yet</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
                        Ask the AI to analyze your project data, find patterns, or summarize findings.
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-2">
                        {["What have we found?", "Summarize observations", "Show me the timeline"].map(q => (
                            <button 
                                key={q}
                                onClick={() => { setInput(q); }}
                                className="text-xs px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full hover:border-purple-500 dark:hover:border-purple-500 transition-colors"
                            >
                                {q}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            
            <AnimatePresence initial={false}>
                {messages.map((msg, idx) => (
                    <motion.div 
                        key={idx} 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                        <div className={`max-w-[85%] p-3.5 rounded-2xl ${
                            msg.role === 'user' 
                                ? 'bg-blue-600 text-white rounded-br-none' 
                                : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-sm border border-gray-100 dark:border-gray-600 rounded-bl-none'
                        }`}>
                            <div className="text-sm leading-relaxed">
                                <StreamingContentRenderer 
                                    content={msg.content} 
                                    isStreaming={msg.role === 'model' && !msg.isComplete}
                                />
                            </div>
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>
            
            {isLoading && messages[messages.length - 1]?.role === 'user' && (
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                >
                    <div className="bg-white dark:bg-gray-700 p-4 rounded-2xl rounded-bl-none shadow-sm border border-gray-100 dark:border-gray-600">
                        <div className="flex gap-1.5">
                            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                            <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                        </div>
                    </div>
                </motion.div>
            )}
            <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-2 relative">
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                        }
                    }}
                    placeholder="Ask about your project..."
                    className="flex-1 p-3 pr-12 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:outline-none resize-none h-[50px] min-h-[50px] max-h-[120px]"
                    disabled={isLoading}
                />
                <button
                    onClick={handleSend}
                    disabled={isLoading || !input.trim()}
                    className="absolute right-2 top-2 p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                >
                    <Send className="w-4 h-4" />
                </button>
            </div>
            <div className="text-center mt-2">
                <span className="text-[10px] text-gray-400">AI can search through all project history</span>
            </div>
        </div>
    </div>
  );
};

export default ProjectReportChat;

